import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6borbghk82orf0ck0g';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Client-side honeypot check
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus('sending');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      id="midnight-video-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
      <p className="mt-2 text-sm text-foreground-600">填写下面的表单，把你的建议或想法告诉我们。</p>

      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
            姓名 <span className="text-accent-400">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={50}
            placeholder="请输入你的姓名"
            className="w-full h-11 px-4 rounded-lg bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
            邮箱 <span className="text-accent-400">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入你的邮箱，方便我们回复"
            className="w-full h-11 px-4 rounded-lg bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">
            留言内容 <span className="text-accent-400">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="想说的都在这里，最多 500 字"
            className="w-full px-4 py-3 rounded-lg bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
          />
        </div>

        <input
          type="text"
          name="website_alt"
          className="withdrawal-note-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill text-lg"></i>
              提交留言
            </>
          )}
        </button>

        {status === 'success' && (
          <div className="flex items-start gap-2 rounded-lg border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
            <i className="ri-checkbox-circle-fill text-lg mt-0.5"></i>
            <span>提交成功，感谢你的反馈！我们会尽快查看并回复。</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-start gap-2 rounded-lg border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-300">
            <i className="ri-error-warning-fill text-lg mt-0.5"></i>
            <span>{formError}</span>
          </div>
        )}
      </div>
    </form>
  );
}